import React, { useMemo } from 'react';
import { BlockButton } from '@/components/BlockButton';
import type { CandidateProfileResponse } from '../types/CandidateProfile';
import useFetchCandidateProfilesCustomFields from '../api/useFetchCandidateProfilesCustomFields';
import {
  normalizeCustomFieldsByCategory,
  type CustomFieldsByCategory,
} from '../../../../utils/normalizeCustomFieldsByCategory';
import { linkifyText } from '../../../../utils/linkify';
import clsx from 'clsx';

type Props = {
  candidateName: string;
  avatarUrl: string;
  customData: CandidateProfileResponse['custom_data'];
  onEditClick: () => void;
};

/**
 * 求職者プロフィールカード表示モード
 */
/**
 * カスタムフィールドの値を表示するコンポーネント
 */
const CustomFieldDisplay = ({
  label,
  value,
  unitName,
}: {
  label: string;
  value: string;
  unitName?: string | null;
}) => {
  return (
    <div className="flex w-full flex-col gap-2">
      <p className="text-sm font-bold">{label}</p>
      <div
        className={clsx(
          'text-sm leading-[1.7] break-words whitespace-pre-wrap',
          !value && 'text-placeholder'
        )}
      >
        {value ? (
          <>
            {linkifyText(
              value,
              'text-link underline hover:text-link hover:underline hover:bg-primary_lighten'
            )}
            {unitName && <span className="ml-1 whitespace-nowrap">{unitName}</span>}
          </>
        ) : (
          '未設定'
        )}
      </div>
    </div>
  );
};

/**
 * カテゴリーフィールド表示用レンダリング
 */
const renderCategoryFieldsDisplay = (
  category: CustomFieldsByCategory,
  customData: CandidateProfileResponse['custom_data']
) => (
  <div className="mt-7 flex flex-col gap-7">
    {(() => {
      let skipNext = false;
      return category.customFields.map((field, index) => {
        if (skipNext) {
          skipNext = false;
          return null;
        }
        const existingData = customData.find((data) => data.custom_field.id === field.fieldId);

        if (field.fieldType === 'long_text') {
          return (
            <div key={field.fieldId} className="w-full">
              <CustomFieldDisplay
                label={field.label}
                value={existingData?.content || ''}
                unitName={field.unitName}
              />
            </div>
          );
        } else {
          const nextField =
            index + 1 < category.customFields.length ? category.customFields[index + 1] : null;
          const isNextShort = nextField && nextField.fieldType !== 'long_text';

          const nextExistingData = nextField
            ? customData.find((data) => data.custom_field.id === nextField.fieldId)
            : null;

          if (isNextShort) {
            skipNext = true;
          }

          return (
            <div key={`row-${index}`} className="flex gap-x-[30px]">
              <CustomFieldDisplay
                label={field.label}
                value={existingData?.content || ''}
                unitName={field.unitName}
              />
              {isNextShort && nextField && (
                <CustomFieldDisplay
                  label={nextField.label}
                  value={nextExistingData?.content || ''}
                  unitName={nextField.unitName}
                />
              )}
            </div>
          );
        }
      });
    })()}
  </div>
);

export default function CandidatesProfileCardDisplay({
  candidateName,
  avatarUrl,
  customData,
  onEditClick,
}: Props) {
  const { data: candidateCustomFields } = useFetchCandidateProfilesCustomFields();

  const normalizedCustomFields = useMemo(
    () => normalizeCustomFieldsByCategory(candidateCustomFields?.records),
    [candidateCustomFields?.records]
  );

  const profileCategory = normalizedCustomFields.find(
    (category) => category.categoryName === 'プロフィール'
  );
  const otherCategories = normalizedCustomFields.filter(
    (category) => category.categoryName !== 'プロフィール'
  );

  return (
    <div className="relative flex flex-col gap-6">
      <div className="bg-white px-10 pt-8 pb-9 shadow-[0px_0px_6px_0px_#00000033]">
        <div className="flex items-center gap-4">
          <p className="text-lg font-bold text-primary">プロフィール</p>
          <div className="w-[84px]">
            <BlockButton
              text={'編集する'}
              height={32}
              icon={'/agent/candidates/icon-edit.svg'}
              fontSize="text-xs"
              onClick={onEditClick}
            />
          </div>
        </div>
        <div className="mt-6 flex items-center gap-6">
          <img
            src={avatarUrl === '' ? '/agent/default-avatar.png' : avatarUrl}
            alt=""
            width={72}
            height={72}
            className="rounded-full"
          />
          <div className="flex items-center gap-4">
            <p className="text-xl font-bold">{candidateName}</p>
          </div>
        </div>
        {profileCategory && renderCategoryFieldsDisplay(profileCategory, customData)}
      </div>

      {/* カスタムフィールドセクション（プロフィール以外） */}
      {otherCategories.map((category) => (
        <div
          key={category.categoryId}
          className="bg-white px-10 pt-8 pb-9 shadow-[0px_0px_6px_0px_#00000033]"
        >
          <div className="flex items-center gap-4">
            <p className="text-lg font-bold text-primary">{category.categoryName}</p>
          </div>
          {renderCategoryFieldsDisplay(category, customData)}
        </div>
      ))}
    </div>
  );
}
